import { ExpirePolicy, PieceAuthorKind } from "../../domain/_common/enums.js";
import { advance, Step } from "../../domain/session/enums.js";
import type { UUID, Stepstamp, LifespanStep, PieceAuthor, PieceLifecycleInput, RelevantAtQuery, AuthoredPiece } from "../../domain/_common/types.js";
export const STEP_ORDER: readonly Step[] = [
  Step.Bootstrap,
  Step.Reflect,
  Step.PlanAssign,
  Step.Write,
  Step.PeerReview,
  Step.Inspection,
  Step.Presentation,
];

export const stepstampFromRoundStep = (input: Stepstamp | null | undefined): number | null => {
  const round = input?.round;
  if (round == null) {
    return null;
  }

  const step = input?.step;
  const stepIndex = step == null ? 0 : STEP_ORDER.indexOf(step);
  if (stepIndex < 0) {
    throw new Error(`Unknown step for stepstamp: ${String(step)}`);
  }

  return (round * 100) + stepIndex;
};

export const StepstampValue = stepstampFromRoundStep;

export const roundStepFromStepstamp = (stepstamp: number): LifespanStep => {
  const round = Math.floor(stepstamp / 100);
  const stepIndex = stepstamp % 100;
  const step = STEP_ORDER[stepIndex];

  if (step == null) {
    throw new Error(`Unknown step index for stepstamp: ${stepstamp}`);
  }

  return { round, step };
};

const expirationResolvers: Record<ExpirePolicy, (input: LifespanStep) => LifespanStep | null> = {
  [ExpirePolicy.AfterTurn]: (input) => {
    const { step, crossedBoundary } = advance(input);
    return { round: crossedBoundary ? input.round + 1 : input.round, step };
  },
  [ExpirePolicy.FullRound]: (input) => {
    const { step, crossedBoundary } = advance(input);
    return { round: crossedBoundary ? input.round + 2 : input.round + 1, step };
  },
  [ExpirePolicy.RoundEnd]: (input) => ({ round: input.round + 1, step: Step.Reflect }),
  [ExpirePolicy.Never]: () => null,
};

export function computeExpiration(
  currentStep: LifespanStep,
  policy: ExpirePolicy,
): LifespanStep | null {
  return expirationResolvers[policy](currentStep);
}

export function authorColumns(author: PieceAuthor): {
  author_member: UUID | null;
  author_user: UUID | null;
} {
  return author.kind === PieceAuthorKind.Member
    ? { author_member: author.memberId, author_user: null }
    : { author_member: null, author_user: author.userId };
}

// Collab table uses swapped column names (`member_author` / `user_author`);
// see the note on `Collab` entity for why.
export function collabAuthorColumns(author: PieceAuthor): {
  member_author: UUID | null;
  user_author: UUID | null;
} {
  return author.kind === PieceAuthorKind.Member
    ? { member_author: author.memberId, user_author: null }
    : { member_author: null, user_author: author.userId };
}

export function buildLifecycleColumns(input: PieceLifecycleInput): {
  relevant_from_stepstamp: number;
  relevant_until_stepstamp: number | null;
} {
  const relevantFromStepstamp = stepstampFromRoundStep(input.currentStep);
  if (relevantFromStepstamp == null) {
    throw new Error('currentStep is required to compute lifecycle columns');
  }
  const relevantUntilStepstamp = input.expiredAt == null ? null : stepstampFromRoundStep(input.expiredAt);
  if (input.expiredAt != null && relevantUntilStepstamp == null) {
    throw new Error('failed to compute expiration stepstamp');
  }

  return {
    relevant_from_stepstamp: relevantFromStepstamp,
    relevant_until_stepstamp: relevantUntilStepstamp,
  };
}

export function relevantAtClause<Q extends RelevantAtQuery<Q>>(
  query: Q,
  relevantAtRaw: Stepstamp | number,
  input: {
    fromCol: string;
    untilCol: string;
    referencedTable?: string;
  },
): Q {
  const relevantAtNumeric = typeof relevantAtRaw === 'number'
    ? relevantAtRaw
    : stepstampFromRoundStep(relevantAtRaw);

  if (relevantAtNumeric == null) {
    return query;
  }

  const expirationFilter = `${input.untilCol}.is.null,${input.untilCol}.gt.${relevantAtNumeric}`;
  return query
    .lte(input.fromCol, relevantAtNumeric)
    .or(
      expirationFilter,
      input.referencedTable == null ? undefined : { referencedTable: input.referencedTable },
    );
}

export function isRelevantAt(
  input: Pick<AuthoredPiece, 'relevantFromStepstamp' | 'relevantUntilStepstamp'>,
  atStep: LifespanStep | number | undefined,
): boolean {
  if (atStep == null) {
    return false;
  }

  const relevantAtStepstamp = typeof atStep === 'number'
    ? atStep
    : stepstampFromRoundStep(atStep);

  if (relevantAtStepstamp == null) {
    return false;
  }

  return input.relevantFromStepstamp <= relevantAtStepstamp
    && (input.relevantUntilStepstamp == null || input.relevantUntilStepstamp > relevantAtStepstamp);
}

export function expireUpdate(currentStep: LifespanStep): {
  relevant_until_stepstamp: number;
} {
  const relevantUntilStepstamp = stepstampFromRoundStep(currentStep);
  if (relevantUntilStepstamp == null) {
    throw new Error('currentStep is required to expire a piece');
  }

  return { relevant_until_stepstamp: relevantUntilStepstamp };
}

export function pinUpdate(): {
  relevant_until_stepstamp: null;
} {
  return { relevant_until_stepstamp: null };
}

export function lifecycleView(input: {
  authorMember: UUID | null;
  authorUser: UUID | null;
  relevantFromStepstamp: number;
  relevantUntilStepstamp: number | null;
}): AuthoredPiece {
  return {
    authorMember: input.authorMember,
    authorUser: input.authorUser,
    relevantFromStepstamp: input.relevantFromStepstamp,
    relevantUntilStepstamp: input.relevantUntilStepstamp,
    createdAtPosition: roundStepFromStepstamp(input.relevantFromStepstamp),
    expiredAtPosition: input.relevantUntilStepstamp == null
      ? null
      : roundStepFromStepstamp(input.relevantUntilStepstamp),
  };
}
